import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Badge } from '../../ui/badge';
import { ArrowRight, TrendingUp, TrendingDown, Minus, Plus, Package } from 'lucide-react';

const formatCurrency = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

export const VersaoCompareModal = ({ open, onClose, versaoAtual, versaoAnterior }) => {
  if (!versaoAtual || !versaoAnterior) return null;

  const diff = (versaoAtual.total || 0) - (versaoAnterior.total || 0);
  const diffPct = versaoAnterior.total ? ((diff / versaoAnterior.total) * 100).toFixed(1) : 0;

  // Map items by nome for comparison
  const getItemKey = (item) => item.produto_nome || item.vessel_model_nome || item.descricao || item.tipo_servico_nome || 'item';
  const anteriorItems = (versaoAnterior.itens || []).map(i => ({ ...i, _key: getItemKey(i) }));
  const atualItems = (versaoAtual.itens || []).map(i => ({ ...i, _key: getItemKey(i) }));

  const anteriorKeys = new Set(anteriorItems.map(i => i._key));
  const atualKeys = new Set(atualItems.map(i => i._key));

  const removed = anteriorItems.filter(i => !atualKeys.has(i._key));
  const added = atualItems.filter(i => !anteriorKeys.has(i._key));
  const modified = atualItems.filter(i => {
    if (!anteriorKeys.has(i._key)) return false;
    const old = anteriorItems.find(o => o._key === i._key);
    if (!old) return false;
    return (old.preco_unitario || old.preco_base || 0) !== (i.preco_unitario || i.preco_base || 0)
      || (old.quantidade || 1) !== (i.quantidade || 1)
      || (old.subtotal || 0) !== (i.subtotal || 0);
  });
  const unchanged = atualItems.filter(i => {
    if (!anteriorKeys.has(i._key)) return false;
    return !modified.find(m => m._key === i._key);
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Comparar Versões
            <Badge variant="outline" className="text-[10px] bg-slate-100">v{versaoAnterior.versao}</Badge>
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-600 border-indigo-200">v{versaoAtual.versao || 'atual'}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Summary diff */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-[10px] text-slate-500">v{versaoAnterior.versao}</p>
              <p className="text-lg font-bold text-slate-600">{formatCurrency(versaoAnterior.total)}</p>
            </div>
            <div className="flex items-center justify-center">
              <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold ${
                diff > 0 ? 'bg-red-50 text-red-600' : diff < 0 ? 'bg-green-50 text-green-600' : 'bg-slate-50 text-slate-500'
              }`}>
                {diff > 0 ? <TrendingUp className="w-4 h-4" /> : diff < 0 ? <TrendingDown className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                {diff > 0 ? '+' : ''}{formatCurrency(diff)}
                <span className="text-[10px] font-normal">({diff > 0 ? '+' : ''}{diffPct}%)</span>
              </div>
            </div>
            <div className="bg-indigo-50 rounded-lg p-3 text-center">
              <p className="text-[10px] text-indigo-500">v{versaoAtual.versao || 'atual'}</p>
              <p className="text-lg font-bold text-indigo-600">{formatCurrency(versaoAtual.total)}</p>
            </div>
          </div>

          {/* Items changes */}
          {added.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Plus className="w-3.5 h-3.5 text-green-500" />
                <span className="text-xs font-semibold text-green-700">Itens adicionados ({added.length})</span>
              </div>
              <div className="space-y-1">
                {added.map((item, i) => (
                  <div key={i} className="flex items-center justify-between bg-green-50 border border-green-100 rounded px-3 py-1.5 text-xs">
                    <div className="flex items-center gap-2">
                      <Package className="w-3.5 h-3.5 text-green-500" />
                      <span className="font-medium">{item._key}</span>
                      {item.lote && <Badge variant="outline" className="text-[8px]">{item.lote}</Badge>}
                    </div>
                    <span className="font-semibold text-green-700">+{formatCurrency(item.subtotal)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {removed.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Minus className="w-3.5 h-3.5 text-red-500" />
                <span className="text-xs font-semibold text-red-700">Itens removidos ({removed.length})</span>
              </div>
              <div className="space-y-1">
                {removed.map((item, i) => (
                  <div key={i} className="flex items-center justify-between bg-red-50 border border-red-100 rounded px-3 py-1.5 text-xs line-through opacity-70">
                    <div className="flex items-center gap-2">
                      <Package className="w-3.5 h-3.5 text-red-400" />
                      <span>{item._key}</span>
                    </div>
                    <span className="text-red-600">-{formatCurrency(item.subtotal)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {modified.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <ArrowRight className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-xs font-semibold text-amber-700">Itens alterados ({modified.length})</span>
              </div>
              <div className="space-y-1">
                {modified.map((item, i) => {
                  const old = anteriorItems.find(o => o._key === item._key);
                  const oldVal = old?.subtotal || 0;
                  const newVal = item.subtotal || 0;
                  const itemDiff = newVal - oldVal;
                  return (
                    <div key={i} className="bg-amber-50 border border-amber-100 rounded px-3 py-1.5 text-xs">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Package className="w-3.5 h-3.5 text-amber-500" />
                          <span className="font-medium">{item._key}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-400 line-through">{formatCurrency(oldVal)}</span>
                          <ArrowRight className="w-3 h-3 text-slate-300" />
                          <span className="font-semibold">{formatCurrency(newVal)}</span>
                          <span className={`text-[10px] ${itemDiff > 0 ? 'text-red-500' : 'text-green-500'}`}>
                            ({itemDiff > 0 ? '+' : ''}{formatCurrency(itemDiff)})
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {unchanged.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Minus className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-xs font-semibold text-slate-500">Itens sem alteração ({unchanged.length})</span>
              </div>
              <div className="space-y-1">
                {unchanged.map((item, i) => (
                  <div key={i} className="flex items-center justify-between bg-slate-50 rounded px-3 py-1.5 text-xs text-slate-500">
                    <span>{item._key}</span>
                    <span>{formatCurrency(item.subtotal)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Conditions diff */}
          {versaoAnterior.desconto_valor !== (versaoAtual.desconto_valor || versaoAtual.desconto_valor === 0 ? versaoAtual.desconto_valor : versaoAnterior.desconto_valor) && (
            <div className="bg-slate-50 rounded-lg p-2 text-xs">
              <span className="text-slate-500">Desconto: </span>
              <span className="line-through text-slate-400">{versaoAnterior.desconto_valor}%</span>
              <ArrowRight className="w-3 h-3 inline mx-1 text-slate-300" />
              <span className="font-medium">{versaoAtual.desconto_valor || 0}%</span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
